import JSXComponent from "../../../dist/jsx-render/JSXComponent";
import dom from "../../../dist/jsx-render/dom";
import { ButtonComponent } from "../ButtonComponent";
import { CheckboxCompoment } from "../CheckboxComponent";
import { DropDownComponentProps, DropDownComponent, OnDropDownChange } from "../DropDownComponent";
import { ListItem, ListItemProps } from "./ListItem";
import { Constants } from "../../Common/constants";
import { app } from "../../Frame/App";

export type ListViewProps = {
    model: any[];
    name?: string;
    additionalCssClasses?: string;

    /** Name of the model property to display for simple
     * use cases. 
     * */
    displayProperty?: string;

    /** optional boolean model field to get/set checked state */
    checkedProperty?: string;

    /** Set a name for the id property. If not set, "id" is used. */
    idProperty?: string;
    stringId?: boolean;

    /** external Action to filter the content for more complex use cases. */
    filterAction?: (searchTerm: string, allItems: ListItem[]) => ListItem[];

    /** external Action to get a string to display for 
     * each list item for more complex use cases.
     * */
    getDisplayNameAction?: (model: any) => string;

    hasMappingType?: boolean;
    mappingTypeChildIdProperty?: string;

    allowFilter?: boolean;
    allowSelect?: boolean;
    allowDelete?: boolean;

    hasIsDeleted?: boolean;
    externalDeleteAction?: (model: any) => void;
    /** Reference to a method that compares two models in more complex use cases. */
    externalCompareModelAction?: (modelA: any, modelB: any) => boolean;

    /** Set a type of a component to display it in the list items
     * for more complex displays of your model.
     * */
    listItemInternalComponentType?: any;
    listItemInternalComponentProps?: any;

    searchTerm?: string;
    selectedIds?: number[];
    updateSelectedIds?: boolean;
    selectedStringIds?: string[];

    postAddEvent?: () => void;
    postDeleteEvent?: () => void;

    preSelectedSearchTerm?: string;
}

export class ListView extends JSXComponent {
    props: ListViewProps;
    constructor(props: ListViewProps) { super(props); }

    items: ListItem[] = [];
    displayedItems: ListItem[] = [];
    element: HTMLElement;

    filterResultsPresent: boolean = false;

    public lastClickedItem: ListItem;

    getSelectedItems = () => {
        return this.items.filter(i => i.props.selected === true);
    }

    getSelectedIds = (): number[] => {
        let items = this.getSelectedItems();

        let ids: number[]

        if (this.props.idProperty && this.props.idProperty.length > 0) {
            ids = items.map(i => Number(i.props.model[this.props.idProperty]));
        } else {
            ids = items.map(i => Number(i.props.model.id));

        }

        return ids;
    }

    getSelectedStringIds = (): string[] => {
        if (this.props.stringId === true) {
            let items = this.getSelectedItems();

            let ids: string[]

            if (this.props.idProperty && this.props.idProperty.length > 0) {
                ids = items.map(i => i.props.model[this.props.idProperty]);
            }

            return ids;
        } else {
            throw new Error("This method can only be used if props -> stringId was set to true.");
        }
    }

    addItem = (item: ListItem) => {
        let ul = this.element.querySelector("ul.list-group");
        ul.appendChild(item.render());

        if (this.props.postAddEvent) this.props.postAddEvent();
    }

    addItems = (items: ListItem[]) => {
        items.forEach(i => this.addItem(i));
    }

    addItemForModel = (model: any) => {
        let ul = this.element.querySelector("ul.list-group");
        ul.appendChild(this.getListItem(model).render());
        this.props.model.push(model);

        if (this.props.postAddEvent) this.props.postAddEvent();
    }

    disable = () => {
        this.items.forEach(i => i.disable());
    }

    enable = () => {
        this.items.forEach(i => i.enable());
    }

    findModel = (model: any): any => {
        let foundItems: any[];
        if (this.props.externalCompareModelAction) {
            foundItems = this.items.filter(i => this.props.externalCompareModelAction(i.props.model, model));
        } else if (this.props.hasMappingType && this.props.mappingTypeChildIdProperty) {
            foundItems = this.items.filter(i => i.props.model[this.props.mappingTypeChildIdProperty] == model.id);
        } else {
            if (this.props.idProperty) {
                foundItems = this.items.filter(i => i.props.model[this.props.idProperty] == model[this.props.idProperty]);
            } else {
                // Assume id property
                foundItems = this.items.filter(i => i.props.model.id == model.id);
            }

        }
        if (foundItems.length > 0) {
            return foundItems[0];
        }

        return null;
    }

    private handleFilter = (event: KeyboardEvent) => {
        this.props.searchTerm = (event.target as HTMLInputElement).value;

        if (this.props.searchTerm && this.props.searchTerm != "") {
            this.setFilter();
        } else {
            // display all content
            let filteredUl = this.element.querySelector("ul.list-group.filtered");
            if (filteredUl) {
                app.destroyDomElements(filteredUl as HTMLElement);
                filteredUl.remove();
            }

            this.element.querySelector("ul.list-group").classList.remove(Constants.cssClass_hidden);
            this.filterResultsPresent = false;
        }
    }

    setFilter = () => {
        this.props.searchTerm = this.props.searchTerm.toLocaleLowerCase();

        if (this.props.displayProperty) {
            this.displayedItems = this.items.filter(i => {
                let value = i.props.model[this.props.displayProperty] as string;
                if (value && value.toLocaleLowerCase().indexOf(this.props.searchTerm) > -1) {
                    return true;
                }
                return false;
            });
        } else if (this.props.filterAction) {
            this.displayedItems = this.props.filterAction(this.props.searchTerm, this.items);
        } else if (this.props.getDisplayNameAction) {
            this.displayedItems = this.items.filter(i => {
                let displayName = this.props.getDisplayNameAction(i.props.model);
                if (displayName.toLocaleLowerCase().indexOf(this.props.searchTerm) > -1) {
                    return true;
                }
                return false;
            });
        }

        this.displayedItems = this.sortItems(this.displayedItems);

        // hide unfiltered ul
        this.element.querySelector("ul.list-group").classList.add(Constants.cssClass_hidden);

        let previouslyFilteredList = this.element.querySelector("ul.list-group.filtered");
        if (previouslyFilteredList) {
            app.destroyDomElements(previouslyFilteredList as HTMLElement);
            previouslyFilteredList.remove();
        }

        this.element.querySelector(".list-items").appendChild(<ul className="list-group filtered">
            {this.displayedItems.map(fi => {
                return fi.render();
            })}
        </ul>);

        if (this.displayedItems.length > 0) {
            this.filterResultsPresent = true;
        }
    }

    /**
     * Sorts the given collection depending on the way the name of the items is retrieved.
     * @param collection
     */
    sortItems = (collection: any[]): any[] => {
        if (this.props.displayProperty) {
            collection.sort((a, b) => app.sortBy(a, b, this.props.displayProperty));
        } else if (this.props.filterAction) {
            // sorting should be done in filter action.
        } else if (this.props.getDisplayNameAction) {
            collection.sort((a, b) =>
                app.sortByName(a.props.getDisplayNameAction(a.props.model),
                    b.props.getDisplayNameAction(b.props.model)));

        }
        return collection;
    }

    renderElements = (): HTMLElement[] => {
        let itemsCollection: any[];
        if (this.props.hasIsDeleted === true) {
            itemsCollection = this.props.model.filter(i => !i.isDeleted);
        } else {
            itemsCollection = this.props.model ?? [];
        }

        var listItems = itemsCollection.map(m => this.getListItem(m));
        listItems = this.sortItems(listItems);
        return listItems.map(l => l.render());
    }

    updateElements = () => {
        let ulElement = this.element.querySelector(".list-group");
        while (ulElement.firstChild) {
            ulElement.removeChild(ulElement.firstChild);
        }
        this.renderElements().forEach(e => ulElement.appendChild(e));
    }

    getListItem = (m: any): ListItem => {
        let props = {
            model: m,
            parentList: this,
            displayProperty: this.props.displayProperty,
            checkedProperty: this.props.checkedProperty,
            idProperty: this.props.idProperty,
            getDisplayNameAction: this.props.getDisplayNameAction,
            allowSelect: this.props.allowSelect,
            allowDelete: this.props.allowDelete,
            deleteItemAction: this.props.externalDeleteAction,
            searchTerm: this.props.searchTerm,
            selectedIds: this.props.selectedIds,
            postDeleteEvent: this.props.postDeleteEvent,
            hasIsDeleted: this.props.hasIsDeleted,
            selected: (this.props.selectedIds && (this.props.selectedIds.indexOf(m.id) > -1 || this.props.selectedIds.indexOf(m.reportId) > -1)) ? true : false,

            listItemInternalComponentType: this.props.listItemInternalComponentType,
            listItemInternalComponentProps: this.props.listItemInternalComponentProps
        }


        return new ListItem(props);
    }

    private isSelected = (model: any) => {
        let selected = false;

        
        if (this.props.stringId) {
            if (this.props.idProperty) {
                selected = (this.props.selectedStringIds && (this.props.selectedStringIds.indexOf(model[this.props.idProperty]) > -1));
            }
        } else {
            if (this.props.idProperty) {
                selected = (this.props.selectedIds && this.props.selectedIds.indexOf(model[this.props.idProperty]) > -1)
            } else {
                // assume .id
                selected = (this.props.selectedIds && this.props.selectedIds.indexOf(model.id) > -1);
            }
        }

        return selected;
    }

    render() {
        this.element = (<div className={"list-view" + (this.props.additionalCssClasses ? " " + this.props.additionalCssClasses : "")}>
            {this.props.name
                ? <label class="form-label">{this.props.name}</label>
                : []}
            {this.props.allowFilter
                ? <div className="form-group">
                    <input type="textbox" class="form-control filter" placeholder={`${Resources.Common.lblContains}...`} />
                </div>
                : []}
            <div className="list-items">
                <ul class="list-group">
                    {...this.renderElements()}
                </ul>
            </div>

        </div>);

        let filterbox = this.element.querySelector("input.filter");
        if (filterbox) {
            filterbox.addEventListener('keyup', this.handleFilter);

            if (this.props.preSelectedSearchTerm && this.props.preSelectedSearchTerm != "") {
                let filterInput = filterbox as HTMLInputElement;
                filterInput.value = this.props.preSelectedSearchTerm;
                this.props.searchTerm = this.props.preSelectedSearchTerm;
            }
        }

        if (this.props.searchTerm && this.props.searchTerm != "") {
            this.setFilter();
        }

        return this.element;
    }
}